import type { Theme } from '../../../theme';

export type LessonType = 'video' | 'file' | 'quiz';

function getLessonItemLayout(theme: Theme) {
  const sc = theme.palette.createCourse;
  const s = theme.spacing;
  const sh = theme.shape;
  const ty = theme.typography;
  return {
    cardPadding: s.sm2,
    cardRadius: sh.borderRadius.sm2,
    cardGap: s.sm2,
    dragSize: sc.dragHandleSize,
    iconSize: sc.iconSize,
    titleFontSize: ty.fontSize.base,
    tagPaddingY: s.xs,
    tagPaddingX: s.sm,
    tagFontSize: ty.fontSize.xs,
    tagRadius: sh.borderRadius.full,
    xpGroupGap: s.xs,
    xpInputWidth: sc.xpInputWidth,
    xpInputPadding: s.xs,
    xpInputFontSize: ty.fontSize.base,
    xpLabelFontSize: ty.fontSize.xs,
    badgeSize: sc.dragHandleSize,
    deleteBtnSize: sc.deleteBtnSize,
    deleteBtnRadius: sh.borderRadius.sm,
    deleteIconSize: sc.dragHandleSize,
  };
}

export interface LessonItemProps {
  theme: Theme;
  type: LessonType;
  typeLabel: string;
  title: string;
  xp: number;
  onXpChange: (xp: number) => void;
  onDelete: () => void;
  deleteAriaLabel: string;
  xpAriaLabel: string;
  lessonTypeIconSrc: string;
  congratulationIconSrc: string;
  deleteIconSrc: string;
  /** Se definido, exibe botão Editar ao lado do Excluir e chama ao clicar */
  onEdit?: () => void;
  editAriaLabel?: string;
  editIconSrc?: string;
  /** Índice da lição (para reorder por drag). */
  lessonIndex?: number;
  /** Lição está sendo arrastada (feedback visual). */
  isLessonDragging?: boolean;
  /** Lição é o alvo do drop (fica mais apagada durante o arraste). */
  isLessonDropTarget?: boolean;
  /** Handlers de drag-and-drop para reorder (opcional). */
  onLessonDragStart?: (e: React.DragEvent, lessonIndex: number) => void;
  onLessonDragOver?: (e: React.DragEvent, lessonIndex: number) => void;
  onLessonDragLeave?: () => void;
  onLessonDrop?: (e: React.DragEvent, targetLessonIndex: number) => void;
  onLessonDragEnd?: () => void;
  /** Label acessível para o handle de arrastar (ex.: "Drag to reorder"). */
  dragHandleAriaLabel?: string;
}

/** Ícone de alça de arrastar: grid 3x3 de pontos */
function DragHandleIcon({ theme }: { theme: Theme }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill={theme.palette.text.secondary}
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      {[0, 1, 2].map(row =>
        [0, 1, 2].map(col => (
          <circle
            key={`${row}-${col}`}
            cx={4 + col * 6}
            cy={4 + row * 6}
            r={1.5}
          />
        ))
      )}
    </svg>
  );
}

/**
 * Um item de lição: drag handle, ícone do tipo, título, tag do tipo,
 * badge XP + input + "XP", botão excluir.
 */
export function LessonItem({
  theme,
  type,
  typeLabel,
  title,
  xp,
  onXpChange,
  onDelete,
  deleteAriaLabel,
  xpAriaLabel,
  lessonTypeIconSrc,
  congratulationIconSrc,
  deleteIconSrc,
  onEdit,
  editAriaLabel,
  editIconSrc,
  lessonIndex = 0,
  isLessonDragging = false,
  isLessonDropTarget = false,
  onLessonDragStart,
  onLessonDragOver,
  onLessonDragLeave,
  onLessonDrop,
  onLessonDragEnd,
  dragHandleAriaLabel = 'Drag to reorder',
}: LessonItemProps) {
  const LAYOUT = getLessonItemLayout(theme);
  const cardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.cardGap,
    padding: LAYOUT.cardPadding,
    backgroundColor: theme.palette.background.paper,
    borderRadius: LAYOUT.cardRadius,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
    opacity: isLessonDragging ? 0.6 : isLessonDropTarget ? 0.5 : 1,
    transition: 'opacity 0.15s ease',
  };

  const dragStyle: React.CSSProperties = {
    width: LAYOUT.dragSize,
    height: LAYOUT.dragSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: onLessonDragStart ? 'grab' : 'default',
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.titleFontSize,
    fontWeight: theme.typography.fontWeight.medium,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    flex: '0 1 auto',
    minWidth: 0,
  };

  const tagStyle: React.CSSProperties = {
    padding: `${LAYOUT.tagPaddingY} ${LAYOUT.tagPaddingX}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.tagFontSize,
    fontWeight: theme.typography.fontWeight.medium,
    lineHeight: 1.25,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: LAYOUT.tagRadius,
    marginLeft: 'auto',
  };

  const xpGroupStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.xpGroupGap,
  };

  const xpInputStyle: React.CSSProperties = {
    width: LAYOUT.xpInputWidth,
    padding: LAYOUT.xpInputPadding,
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.xpInputFontSize,
    lineHeight: 1.25,
    textAlign: 'center',
    border: `1px solid ${theme.palette.border.input}`,
    borderRadius: LAYOUT.deleteBtnRadius,
    outline: 'none',
    backgroundColor: theme.palette.background.paper,
    boxSizing: 'border-box',
  };

  const xpLabelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: LAYOUT.xpLabelFontSize,
    lineHeight: 1.25,
    color: theme.palette.text.secondary,
  };

  const deleteBtnStyle: React.CSSProperties = {
    width: LAYOUT.deleteBtnSize,
    height: LAYOUT.deleteBtnSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: LAYOUT.deleteBtnRadius,
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
    flexShrink: 0,
  };

  const handleDragOver = (e: React.DragEvent) => {
    onLessonDragOver?.(e, lessonIndex);
  };

  const handleDrop = (e: React.DragEvent) => {
    onLessonDrop?.(e, lessonIndex);
  };

  return (
    <article
      style={cardStyle}
      data-lesson-type={type}
      onDragOver={onLessonDragOver ? handleDragOver : undefined}
      onDragLeave={onLessonDragLeave}
      onDrop={onLessonDrop ? handleDrop : undefined}
    >
      <div
        role="button"
        tabIndex={onLessonDragStart ? 0 : undefined}
        style={dragStyle}
        aria-label={dragHandleAriaLabel}
        title={dragHandleAriaLabel}
        draggable={Boolean(onLessonDragStart)}
        onDragStart={
          onLessonDragStart
            ? (e: React.DragEvent) => onLessonDragStart(e, lessonIndex)
            : undefined
        }
        onDragEnd={onLessonDragEnd}
      >
        <DragHandleIcon theme={theme} />
      </div>
      <img
        src={lessonTypeIconSrc}
        alt=""
        width={LAYOUT.iconSize}
        height={LAYOUT.iconSize}
        style={{ objectFit: 'contain', flexShrink: 0 }}
        aria-hidden
      />
      <span style={titleStyle}>{title}</span>
      <span style={tagStyle}>{typeLabel}</span>
      <div style={xpGroupStyle}>
        <img
          src={congratulationIconSrc}
          alt=""
          width={LAYOUT.badgeSize}
          height={LAYOUT.badgeSize}
          style={{ objectFit: 'contain', flexShrink: 0 }}
          aria-hidden
        />
        <input
          type="number"
          min={0}
          value={xp}
          onChange={e =>
            onXpChange(Math.max(0, parseInt(e.target.value, 10) || 0))
          }
          style={xpInputStyle}
          aria-label={xpAriaLabel}
        />
        <span style={xpLabelStyle}>XP</span>
      </div>
      {onEdit && editIconSrc && (
        <button
          type="button"
          style={deleteBtnStyle}
          onClick={onEdit}
          aria-label={editAriaLabel}
          title={editAriaLabel}
        >
          <img
            src={editIconSrc}
            alt=""
            width={LAYOUT.deleteIconSize}
            height={LAYOUT.deleteIconSize}
            style={{ objectFit: 'contain' }}
            aria-hidden
          />
        </button>
      )}
      <button
        type="button"
        style={deleteBtnStyle}
        onClick={onDelete}
        aria-label={deleteAriaLabel}
        title={deleteAriaLabel}
      >
        <img
          src={deleteIconSrc}
          alt=""
          width={LAYOUT.deleteIconSize}
          height={LAYOUT.deleteIconSize}
          style={{ objectFit: 'contain' }}
          aria-hidden
        />
      </button>
    </article>
  );
}
